import {create} from 'zustand';
import {measurementApi} from '../services/api/measurementApi';
import {optionTypeApi} from '../services/api/optionTypeApi';

export const useMeasurementStore = create((set, get) => ({
  catalogItems: [],
  currentCatalogItem: null,
  customerProfiles: [],
  measurementTypes: [],
  bodyProfile: null,
  catalogItemOptionTypes: [],
  measurementValues: [],
  isLoading: false,

  fetchCatalogItems: async () => {
    set({isLoading: true});
    try {
      const response = await measurementApi.listCatalogItems();
      set({catalogItems: response.data.data});
    } finally {
      set({isLoading: false});
    }
  },

  fetchCatalogItemDetail: async id => {
    set({isLoading: true});
    try {
      const response = await measurementApi.getCatalogItem(id);
      set({currentCatalogItem: response.data.data});
    } finally {
      set({isLoading: false});
    }
  },

  createCatalogItem: async data => {
    const response = await measurementApi.createCatalogItem(data);
    const newType = response.data.data;
    set(state => ({catalogItems: [...state.catalogItems, newType]}));
    return newType;
  },

  updateCatalogItem: async (id, data) => {
    const response = await measurementApi.updateCatalogItem(id, data);
    const updated = response.data.data;
    set(state => ({
      catalogItems: state.catalogItems.map(dt => (dt.id === id ? updated : dt)),
    }));
    return updated;
  },

  deleteCatalogItem: async (id) => {
    await measurementApi.deleteCatalogItem(id);
    set(state => ({
      catalogItems: state.catalogItems.filter(dt => dt.id !== id),
    }));
  },

  fetchCustomerProfiles: async (customerId, catalogItemId) => {
    set({isLoading: true});
    try {
      const response = await measurementApi.listProfiles(customerId, catalogItemId);
      set({customerProfiles: response.data.data});
    } finally {
      set({isLoading: false});
    }
  },

  createProfile: async (customerId, data) => {
    const response = await measurementApi.createProfile(customerId, data);
    const newProfile = response.data.data;
    set(state => ({
      customerProfiles: [...state.customerProfiles, newProfile],
    }));
    return newProfile;
  },

  updateProfile: async (customerId, profileId, data) => {
    const response = await measurementApi.updateProfile(
      customerId,
      profileId,
      data,
    );
    const updated = response.data.data;
    set(state => ({
      customerProfiles: state.customerProfiles.map(p =>
        p.id === profileId ? updated : p,
      ),
    }));
    return updated;
  },

  deleteProfile: async (customerId, profileId) => {
    await measurementApi.deleteProfile(customerId, profileId);
    set(state => ({
      customerProfiles: state.customerProfiles.filter(p => p.id !== profileId),
    }));
  },

  // Field management
  addFieldToCatalogItem: async (catalogItemId, data) => {
    const response = await measurementApi.addField(catalogItemId, data);
    return response.data.data;
  },

  updateField: async (catalogItemId, fieldId, data) => {
    const response = await measurementApi.updateField(catalogItemId, fieldId, data);
    return response.data.data;
  },

  removeFieldFromCatalogItem: async (catalogItemId, fieldId) => {
    await measurementApi.removeField(catalogItemId, fieldId);
  },

  reorderFields: async (catalogItemId, orderedIds) => {
    await measurementApi.reorderFields(catalogItemId, orderedIds);
  },

  // Option type mappings
  fetchOptionTypesForCatalogItem: async (catalogItemId) => {
    set({isLoading: true});
    try {
      const response = await measurementApi.getOptionTypes(catalogItemId);
      set({catalogItemOptionTypes: response.data.data});
    } finally {
      set({isLoading: false});
    }
  },

  toggleOptionType: async (catalogItemId, optionTypeId, selected) => {
    const response = await measurementApi.toggleOptionType(catalogItemId, {
      optionTypeId,
      selected,
    });
    return response.data.data;
  },

  reorderOptionTypes: async (catalogItemId, orderedIds) => {
    await measurementApi.reorderOptionTypes(catalogItemId, orderedIds);
  },

  // Measurement values (catalog item option values for MEASUREMENT_TYPE)
  fetchMeasurementValues: async (catalogItemId) => {
    set({isLoading: true});
    try {
      const response = await measurementApi.getMeasurementValues(catalogItemId);
      set({measurementValues: response.data.data});
    } finally {
      set({isLoading: false});
    }
  },

  toggleMeasurementValue: async (catalogItemId, valueId, data) => {
    const response = await measurementApi.toggleMeasurementValue(catalogItemId, valueId, data);
    return response.data.data;
  },

  toggleMeasurementByGlobalOption: async (catalogItemId, globalOptionId, selected) => {
    const response = await measurementApi.toggleMeasurementByGlobalOption(catalogItemId, {
      globalOptionId,
      selected,
    });
    set({measurementValues: response.data.data});
    return response.data.data;
  },

  reorderMeasurementValues: async (catalogItemId, orderedIds) => {
    await measurementApi.reorderMeasurementValues(catalogItemId, orderedIds);
  },

  clearCurrentCatalogItem: () => set({currentCatalogItem: null}),
  clearCustomerProfiles: () => set({customerProfiles: []}),
  clearCatalogItemOptionTypes: () => set({catalogItemOptionTypes: []}),
  clearMeasurementValues: () => set({measurementValues: []}),

  // Measurement types (fetched from MEASUREMENT_TYPE global option type via consolidated endpoint)
  fetchMeasurementTypes: async (genderFilter) => {
    set({isLoading: true});
    try {
      const response = await optionTypeApi.listConsolidatedOptionTypes(null, 'MEASUREMENT');
      const allOptionTypes = response.data.data;
      const mtType = allOptionTypes.find(ot => ot.code === 'MEASUREMENT_TYPE');
      let options = mtType ? mtType.options : [];
      if (genderFilter) {
        options = options.filter(o => o.gender === genderFilter || o.gender === 'BOTH');
      }
      set({measurementTypes: options});
    } finally {
      set({isLoading: false});
    }
  },

  // Body measurement profiles
  fetchBodyProfile: async (customerId) => {
    try {
      const response = await measurementApi.getBodyProfile(customerId);
      set({bodyProfile: response.data.data});
    } catch (e) {
      if (e.response?.status === 404) {
        set({bodyProfile: null});
      }
    }
  },

  saveBodyProfile: async (customerId, data) => {
    const response = await measurementApi.saveBodyProfile(customerId, data);
    const profile = response.data.data;
    set({bodyProfile: profile});
    return profile;
  },

  clearBodyProfile: () => set({bodyProfile: null}),
}));
